/* Site-Specific JavaScript */

/**
 * axe td-has-header / table-fake-caption: BusinessWire earnings tables in
 * news articles ship as td-only grids with a full-width title row. Move the
 * first such row into a real caption. Later full-width rows are section
 * headers: each gets its own tbody so scope=rowgroup applies only there.
 * Then convert column/row labels to th with scope. Own rows only (no nested tables).
 */
(function () {
    var MONTH =
        'January|February|March|April|May|June|July|August|September|October|November|December';

    function cellText(cell) {
        return (cell.innerText || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
    }

    function ownRows(table) {
        return Array.prototype.slice.call(
            table.querySelectorAll(':scope > thead > tr, :scope > tbody > tr, :scope > tfoot > tr, :scope > tr')
        );
    }

    function isNestedTable(table) {
        return !!(table.parentElement && table.parentElement.closest('table'));
    }

    function columnCount(rows) {
        var max = 0;
        rows.forEach(function (tr) {
            var n = 0;
            Array.prototype.forEach.call(tr.cells, function (cell) {
                n += cell.colSpan || 1;
            });
            if (n > max) max = n;
        });
        return max;
    }

    function isYear(text) {
        return /^(19|20)\d{2}$/.test(text);
    }

    function looksLikeHeaderLabel(text) {
        if (!text) return false;
        if (isYear(text)) return true;
        if (new RegExp('^(' + MONTH + ')\\.?\\s+\\d{1,2},?$', 'i').test(text)) return true;
        if (/months ended/i.test(text)) return true;
        if (/^vs\.?\s*(19|20)?\d{2}$/i.test(text)) return true;
        if (/increase\s*\/\s*\(decrease\)/i.test(text)) return true;
        if (/^(Q[1-4]|FY)\s*(19|20)?\d{2}$/i.test(text)) return true;
        return false;
    }

    function looksLikeDataValue(text) {
        if (!text || looksLikeHeaderLabel(text)) return false;
        if (/^[\$%()pts.]+$/i.test(text)) return false;
        // "Ownership(1)" is a label with a footnote, not a numeric value.
        // Accounting negatives like "(21)" have no letters and still count as data.
        var forDigits = /[A-Za-z]/.test(text) ? text.replace(/\(\d{1,2}\)/g, '') : text;
        return /\d/.test(forDigits);
    }

    function looksLikeUnit(text) {
        return /^[\$%()]+$/.test(text);
    }

    function classifyRow(tr, cols) {
        var cells = Array.prototype.slice.call(tr.cells);
        var texts = cells.map(cellText);
        var nonempty = texts.filter(Boolean);
        if (!nonempty.length) return 'spacer';

        var restData = texts.slice(1).some(looksLikeDataValue);
        var colSpan0 = cells[0] ? cells[0].colSpan || 1 : 1;
        if (cells.length === 1 && colSpan0 >= cols && cols >= 3) return 'title';
        if (restData) return 'data';
        if (texts[0] && !restData && texts.slice(1).every(function (t) { return !t; })) {
            return 'section';
        }
        return 'colheader';
    }

    // Only the first full-width row is a table title. Later ones are section headers.
    function classifyRows(rows, cols) {
        var sawTitle = false;
        return rows.map(function (tr) {
            var kind = classifyRow(tr, cols);
            if (kind !== 'title') return kind;
            if (sawTitle) return 'section';
            sawTitle = true;
            return 'title';
        });
    }

    function replaceWithTh(td, scope) {
        var th = document.createElement('th');
        Array.prototype.forEach.call(td.attributes, function (attr) {
            th.setAttribute(attr.name, attr.value);
        });
        th.setAttribute('scope', scope);
        th.innerHTML = td.innerHTML;
        td.parentNode.replaceChild(th, td);
        return th;
    }

    function moveTitleToCaption(table, tr) {
        var cell = tr.cells[0];
        if (!cell || !cellText(cell)) return;
        if (table.querySelector(':scope > caption')) return;

        var caption = document.createElement('caption');
        caption.className = 'article-table-caption';
        caption.innerHTML = cell.innerHTML;
        table.insertBefore(caption, table.firstChild);

        var parent = tr.parentNode;
        parent.removeChild(tr);
        if (parent.tagName === 'TBODY' && !parent.rows.length) {
            parent.parentNode.removeChild(parent);
        }
    }

    function sectionScope(tr) {
        var body = tr.parentNode;
        if (body && body.tagName === 'TBODY' && body.rows.length > 1) {
            return 'rowgroup';
        }
        return 'row';
    }

    // Col headers go in thead. Each later section starts its own tbody so
    // scope=rowgroup applies only to that section, not the whole table.
    function regroupRows(table, rows, kinds) {
        var caption = table.querySelector(':scope > caption');
        var colheaders = [];
        var groups = [];
        var current = { section: null, rows: [] };

        rows.forEach(function (tr, i) {
            if (!tr.parentNode) return;
            var kind = kinds[i];
            if (kind === 'title') return;
            if (kind === 'colheader') {
                colheaders.push(tr);
                return;
            }
            if (kind === 'section') {
                if (current.section || current.rows.length) {
                    groups.push(current);
                }
                current = { section: tr, rows: [] };
                return;
            }
            current.rows.push(tr);
        });
        if (current.section || current.rows.length) {
            groups.push(current);
        }

        if (colheaders.length) {
            var thead = table.tHead;
            if (!thead) {
                thead = document.createElement('thead');
                table.insertBefore(thead, caption ? caption.nextSibling : table.firstChild);
            }
            colheaders.forEach(function (tr) {
                thead.appendChild(tr);
            });
        }

        var tfoot = table.tFoot;
        var oldBodies = Array.prototype.slice.call(table.tBodies);
        groups.forEach(function (group) {
            var tbody = document.createElement('tbody');
            table.insertBefore(tbody, tfoot);
            if (group.section) tbody.appendChild(group.section);
            group.rows.forEach(function (tr) {
                tbody.appendChild(tr);
            });
        });
        oldBodies.forEach(function (body) {
            if (body.parentNode && !body.rows.length) {
                body.parentNode.removeChild(body);
            }
        });
    }

    function enhanceTable(table) {
        if (table.getAttribute('data-headers-enhanced') === '1') return;
        if (isNestedTable(table) || table.classList.contains('report')) return;

        var rows = ownRows(table);
        var cols = columnCount(rows);
        if (rows.length < 3 || cols < 3) return;

        var kinds = classifyRows(rows, cols);
        table.setAttribute('data-headers-enhanced', '1');

        rows.forEach(function (tr, i) {
            if (kinds[i] === 'title') {
                moveTitleToCaption(table, tr);
            }
        });

        regroupRows(table, rows, kinds);

        rows.forEach(function (tr, i) {
            if (!tr.parentNode) return;
            var kind = kinds[i];
            var cells = Array.prototype.slice.call(tr.cells);

            if (kind === 'colheader') {
                cells.forEach(function (cell) {
                    if (cell.tagName !== 'TD' || !cellText(cell)) return;
                    var cs = cell.colSpan || 1;
                    replaceWithTh(cell, cs > 1 ? 'colgroup' : 'col');
                });
                return;
            }

            if (kind === 'section' || kind === 'data') {
                var first = cells[0];
                if (
                    first &&
                    first.tagName === 'TD' &&
                    cellText(first) &&
                    !looksLikeUnit(cellText(first))
                ) {
                    replaceWithTh(
                        first,
                        kind === 'section' ? sectionScope(tr) : 'row'
                    );
                }
            }
        });
    }

    function run() {
        document.querySelectorAll('.full-news-article table').forEach(enhanceTable);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', run);
    } else {
        run();
    }
})();

/**
 * axe p-as-heading: BusinessWire deks ship as <p class="bwalignc"><i><b>…</b></i></p>.
 * Convert paragraphs that fail the axe check into h2, preserving inner markup.
 */
(function () {
    var MARGINS = [
        { weight: 150, italic: true },
        { weight: 150, size: 1.15 },
        { italic: true, size: 1.15 },
        { size: 1.4 }
    ];
    var FAIL_LENGTH = 0.5;

    function normalizeFontWeight(weight) {
        if (weight === 'lighter') return 100;
        if (weight === 'normal') return 400;
        if (weight === 'bold') return 700;
        if (weight === 'bolder') return 900;
        weight = parseInt(weight, 10);
        return !isNaN(weight) ? weight : 400;
    }

    function getTextContainer(elm) {
        var nextNode = elm;
        var outerText = (elm.textContent || '').trim();
        var innerText = outerText;

        while (innerText === outerText && nextNode !== undefined) {
            var i = -1;
            elm = nextNode;
            if (!elm.children || elm.children.length === 0) {
                return elm;
            }
            do {
                i++;
                innerText = elm.children[i]
                    ? (elm.children[i].textContent || '').trim()
                    : '';
            } while (innerText === '' && i + 1 < elm.children.length);
            nextNode = elm.children[i];
        }

        return elm;
    }

    function getStyleValues(node) {
        var style = window.getComputedStyle(getTextContainer(node));
        return {
            fontWeight: normalizeFontWeight(style.getPropertyValue('font-weight')),
            fontSize: parseInt(style.getPropertyValue('font-size'), 10),
            isItalic: style.getPropertyValue('font-style') === 'italic'
        };
    }

    function isHeaderStyle(styleA, styleB) {
        return MARGINS.some(function (margin) {
            return (
                (!margin.size || styleA.fontSize / margin.size > styleB.fontSize) &&
                (!margin.weight ||
                    styleA.fontWeight - margin.weight > styleB.fontWeight) &&
                (!margin.italic || (styleA.isItalic && !styleB.isItalic))
            );
        });
    }

    function nextParagraphSibling(p) {
        var siblings = Array.prototype.slice.call(p.parentNode.children);
        var index = siblings.indexOf(p);
        var i;
        for (i = index + 1; i < siblings.length; i++) {
            if (siblings[i].nodeName === 'P') return siblings[i];
        }
        return null;
    }

    function wouldFailPAsHeading(p) {
        if (p.closest('table, blockquote')) return false;
        var next = nextParagraphSibling(p);
        if (!next) return false;

        var headingLength = (p.textContent || '').trim().length;
        var paragraphLength = (next.textContent || '').trim().length;
        if (!headingLength || !paragraphLength) return false;
        if (headingLength > paragraphLength * FAIL_LENGTH) return false;
        if (!isHeaderStyle(getStyleValues(p), getStyleValues(next))) return false;
        return true;
    }

    function pToHeading(p) {
        var heading = document.createElement('h2');
        Array.prototype.forEach.call(p.attributes, function (attr) {
            heading.setAttribute(attr.name, attr.value);
        });
        heading.classList.add('article-subhead');
        heading.innerHTML = p.innerHTML;
        p.parentNode.replaceChild(heading, p);
    }

    function run() {
        document.querySelectorAll('.full-news-article p').forEach(function (p) {
            if (wouldFailPAsHeading(p)) {
                pToHeading(p);
            }
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', run);
    } else {
        run();
    }
})();
